import { useEffect, useState, useCallback, useRef } from 'react';
import { ExternalLink, Send, Lock, CheckCircle2, CheckSquare, Globe, Paperclip, X } from 'lucide-react';
import { Modal, Button, TextField, Select, Spinner, Avatar, Checkbox } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { gatewayApi } from '../../services/apiService';
import { ticketService } from '../../services/ticketService';
import { StatusBadge, PriorityBadge, timeAgo } from './ticketBadges';
import { useTicketOptions } from './ticketOptions';

function userName(u, fallback) {
  return u?.fullName || u?.full_name || u?.name || u?.email || fallback;
}

export default function TicketDetailModal({ ticketId, open, onClose, onChanged }) {
  const { hasPermission, user } = useAuth();
  const { status: statusOptions } = useTicketOptions();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]);
  const replyFileRef = useRef(null);

  const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/x-msvideo,video/quicktime,video/webm';
  const addReplyFiles = (list) => {
    if (!list) return;
    // Capture the FileList synchronously: the input is reset right after this
    // call (to allow re-picking the same file), which empties e.target.files
    // before a lazy Array.from inside the state updater would read it.
    const picked = Array.from(list);
    if (!picked.length) return;
    setReplyFiles((prev) => [...prev, ...picked]);
  };
  const removeReplyFile = (idx) => setReplyFiles((prev) => prev.filter((_, i) => i !== idx));

  const canUpdate = hasPermission('support-service', 'ticket', 'update');
  const canRespondAny = hasPermission('support-service', 'ticket', 'respond');
  const myId = String(user?.id || '');

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const body = await ticketService.getById(ticketId);
      setTicket(body?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (open) load();
    else { setTicket(null); setReply(''); setReplyFiles([]); setInternal(false); }
  }, [open, load]);

  const submitReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const message = await ticketService.addMessage(ticketId, { body: reply, is_internal: internal });
      const messageId = message?.data?.id || message?.id;
      // Attach the picked files to the freshly-created message.
      if (messageId && replyFiles.length) {
        for (const f of replyFiles) {
          try {
            await ticketService.uploadAttachment(ticketId, f, messageId);
          } catch {
            toast.error(`Failed to upload ${f.name}`);
          }
        }
      }
      setReply('');
      setReplyFiles([]);
      setInternal(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      await ticketService.updateStatus(ticketId, status);
      toast.success('Status updated');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to update status');
    }
  };

  const downloadAttachment = async (a) => {
    try {
      const res = await gatewayApi.get(`/support-service/tickets/${ticketId}/attachments/${a.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.original_name || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      showCloseButton
      style={{ padding: 0, backgroundColor: 'var(--palette-background-default)' }}
    >
      {loading || !ticket ? (
        <div className="py-20 flex justify-center"><Spinner size="large" color="primary" /></div>
      ) : (
        <div className="flex max-h-[calc(100dvh-1rem)] min-h-0 flex-col sm:max-h-[85vh]">
          {/* Header (pr-12 leaves room for the modal's close button) */}
          <div className="flex shrink-0 flex-col items-stretch gap-3 border-b border-divider px-4 py-4 pr-12 sm:flex-row sm:items-start sm:justify-between sm:px-6">
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-fg-tertiary">{ticket.ticket_number}</span>
                <StatusBadge status={ticket.status} />
                <PriorityBadge priority={ticket.priority} />
                {ticket.task_id && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-ui-md bg-primary-overlay text-primary border border-primary-border">
                    <CheckSquare size={12} /> Task #{ticket.task_id}{ticket.task?.status ? ` · ${ticket.task.status}` : ''}
                  </span>
                )}
              </div>
              <h2 className="break-words text-xl font-bold text-fg-primary">{ticket.title}</h2>
              <div className="flex items-center gap-3 text-xs text-fg-tertiary flex-wrap">
                {ticket.author && (
                  <span className="inline-flex items-center gap-1.5">
                    <Avatar name={userName(ticket.author, ticket.author_id)} src={ticket.author.avatar} size="small" />
                    {userName(ticket.author, ticket.author_id)}
                  </span>
                )}
                {ticket.service_name && (
                  <span className="inline-flex items-center gap-1"><Globe size={12} /> {ticket.service_name}</span>
                )}
                {ticket.source_url && (
                  <a href={ticket.source_url} className="inline-flex min-h-10 min-w-0 max-w-full items-center gap-1 rounded-ui-full border border-divider bg-surface-default px-2 py-1 text-primary transition-colors hover:bg-action-hover">
                    <span className="truncate">{ticket.source_label || 'Source page'}</span> <ExternalLink size={11} className="shrink-0" />
                  </a>
                )}
              </div>
            </div>

            {/* Status — editable here (clearly an action), not just the badge above */}
            {canUpdate && (
              <div className="w-full shrink-0 sm:w-44">
                <Select label="Change status" options={statusOptions} value={ticket.status} onChange={(v) => v && changeStatus(v)} fullWidth size="small" />
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
            <p className="break-words whitespace-pre-wrap rounded-ui-lg border border-divider bg-surface-default p-3 text-sm text-fg-primary">
            {ticket.description}
          </p>

          {Array.isArray(ticket.attachments) && ticket.attachments.length > 0 && (
            <div className="space-y-1.5 border-t border-divider pt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary">Attachments</h3>
              <div className="flex flex-wrap gap-2">
                {ticket.attachments.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => downloadAttachment(a)}
                    className="inline-flex min-h-10 max-w-[14rem] items-center gap-1.5 rounded-ui-md border border-divider bg-surface-default px-2.5 py-1.5 text-xs text-fg-secondary transition-colors hover:border-primary/60 hover:text-primary"
                  >
                    <span className="truncate">{a.original_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation */}
          <div className="space-y-3 border-t border-divider pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary">Conversation</h3>
            {(ticket.messages || []).length === 0 ? (
              <p className="text-sm text-fg-tertiary">No messages yet.</p>
            ) : (
              <div className="space-y-3">
                {ticket.messages.map((m) => {
                  if (m.is_system) {
                    return (
                      <div key={m.id} className="flex justify-center">
                        <span className="inline-flex max-w-full flex-wrap items-center justify-center gap-1.5 break-words rounded-ui-full border border-success/30 bg-success/10 px-3 py-1 text-center text-xs text-success">
                          <CheckCircle2 size={14} /> {m.body}
                        </span>
                      </div>
                    );
                  }
                  const mine = String(m.author_id) === myId;
                  const name = userName(m.author, m.author_id);
                  return (
                    <div key={m.id} className={`flex min-w-0 gap-2 ${mine ? 'flex-row-reverse' : ''}`}>
                      <Avatar name={name} src={m.author?.avatar} size="small" />
                      <div className={`min-w-0 max-w-[calc(100%_-_2.5rem)] sm:max-w-[78%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                        <div className={`mb-0.5 flex flex-wrap items-center gap-2 text-[11px] text-fg-tertiary ${mine ? 'flex-row-reverse' : ''}`}>
                          <span className="font-medium text-fg-secondary">{mine ? 'You' : name}</span>
                          {m.is_internal && <span className="inline-flex items-center gap-0.5 text-warning"><Lock size={10} /> internal</span>}
                          <span>{timeAgo(m.created_at)}</span>
                        </div>
                        <div className={`max-w-full break-words rounded-ui-lg border px-3 py-2 text-sm whitespace-pre-wrap
                          ${m.is_internal
                            ? 'bg-warning/5 border-warning/40 text-fg-primary'
                            : mine
                              ? 'bg-primary text-primary-contrast-text border-transparent'
                              : 'bg-surface-default border-divider text-fg-primary'}`}>
                          {m.body}
                        </div>
                        {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                          <div className={`mt-1 flex flex-wrap gap-1.5 ${mine ? 'justify-end' : ''}`}>
                            {m.attachments.map((a) => (
                              <button
                                key={a.id}
                                type="button"
                                onClick={() => downloadAttachment(a)}
                                className="inline-flex min-h-10 max-w-[12rem] items-center gap-1.5 rounded-ui-md border border-divider bg-surface-default px-2 py-1 text-xs text-fg-secondary transition-colors hover:border-primary/60 hover:text-primary"
                              >
                                <Paperclip size={12} className="shrink-0" />
                                <span className="truncate">{a.original_name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          </div>

          {/* Reply — compose bar: textarea on top, toolbar (attach + internal /
              send) at the bottom, attachment chips between them. */}
          <div className="shrink-0 space-y-2 border-t border-divider p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6">
            <TextField
              fullWidth
              multiline
              rows={3}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write a reply…"
            />

            {replyFiles.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {replyFiles.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-xs px-2 py-1 rounded-ui-md bg-surface-default border border-divider text-fg-secondary">
                    <Paperclip size={12} className="shrink-0" />
                    <span className="truncate max-w-[12rem]">{f.name}</span>
                    <button type="button" onClick={() => removeReplyFile(i)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded text-fg-tertiary hover:bg-action-hover hover:text-error" aria-label="Remove">
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <input
              ref={replyFileRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => { addReplyFiles(e.target.files); if (replyFileRef.current) replyFileRef.current.value = ''; }}
            />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <Button variant="outlined" color="neutral" size="small" onClick={() => replyFileRef.current?.click()} className="min-h-10">
                  <Paperclip size={14} /> Attach
                </Button>
                {canRespondAny && (
                  <Checkbox
                    label="Internal note"
                    checked={internal}
                    onChange={(e) => setInternal(e?.target ? e.target.checked : !!e)}
                  />
                )}
              </div>
              <Button color="primary" disabled={sending || !reply.trim()} onClick={submitReply} className="w-full sm:w-auto">
                <Send size={15} /> {sending ? '…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
